import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import "./Settings.css";
import API_URL from "./config";

const Settings = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [useTeamColors, setUseTeamColors] = useState(true);
  const [injuryReportSetting, setInjuryReportSetting] = useState("yourTeam");

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (!userData) {
      navigate("/login");
      return;
    }
    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);
    setEmail(parsedUser.email || "");

    // Load theme preference
    const savedTheme = localStorage.getItem("theme") || "dark";
    const isDark = savedTheme === "dark";
    setIsDarkMode(isDark);
    applyTheme(isDark);

    // Load injury report preference
    const savedInjuryReport = localStorage.getItem("injuryReportSetting") || "yourTeam";
    setInjuryReportSetting(savedInjuryReport);

    // Load team colors preference (default to true for backward compatibility)
    const savedUseTeamColors = localStorage.getItem("useTeamColors") !== "false";
    setUseTeamColors(savedUseTeamColors);
  }, [navigate]);



  const applyTheme = (isDark) => {
    const theme = isDark ? "dark" : "light";
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);
  };

  const handleThemeToggle = () => {
    const newIsDark = !isDarkMode;
    setIsDarkMode(newIsDark);
    applyTheme(newIsDark);
  };

  const handleTeamColorsToggle = () => {
    const newUseTeamColors = !useTeamColors;
    setUseTeamColors(newUseTeamColors);
    localStorage.setItem("useTeamColors", newUseTeamColors);
    // Dispatch custom event to notify other components of the setting change
    window.dispatchEvent(new CustomEvent("teamColorsSettingChanged", { detail: { useTeamColors: newUseTeamColors } }));
  };

  const handleInjuryReportChange = (setting) => {
    setInjuryReportSetting(setting);
    localStorage.setItem("injuryReportSetting", setting);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    const PASSWORD_LIMITS = { min: 8, max: 32 };
    
    // Validate password length
    if (newPassword.length < PASSWORD_LIMITS.min || newPassword.length > PASSWORD_LIMITS.max) {
      setMessage(`Password must be between ${PASSWORD_LIMITS.min} and ${PASSWORD_LIMITS.max} characters`);
      setMessageType("error");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setMessage("New passwords do not match");
      setMessageType("error");
      return;
    }
    
    if (currentPassword === newPassword) {
      setMessage("New password cannot be the same as your current password");
      setMessageType("error");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      // Token loaded from storage
      
      if (!token) {
        setMessage("Session expired. Please login again.");
        setMessageType("error");
        setLoading(false);
        return;
      }
      
      const response = await fetch(`${API_URL}/api/auth/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          currentPassword,
          newPassword,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage("Password changed successfully");
        setMessageType("success");
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
      } else {
        setMessage(data.detail || data.message || "Failed to change password");
        setMessageType("error");
      }
    } catch (err) {
      // Password change error
      setMessage("Error changing password: " + err.message);
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };







  const handleDeleteAccount = async () => {
    if (!window.confirm("Are you sure you want to delete your account? This action cannot be undone.")) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${API_URL}/api/auth/delete-account`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        localStorage.removeItem("user");
        localStorage.removeItem("access_token");
        localStorage.removeItem("activeSaveId");
        navigate("/login");
      } else {
        setMessage("Failed to delete account");
        setMessageType("error");
      }
    } catch (err) {
      setMessage("Error deleting account: " + err.message);
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <>
      <Navbar />
      <div className="settings-container">
        <div className="settings-card">
          <h2>Settings</h2>

          <div className="settings-section">
            <h3>Account Information</h3>
            <div className="setting-item">
              <label>Username</label>
              <p className="setting-value">{user.username || "N/A"}</p>
            </div>
            <div className="setting-item">
              <label>Email</label>
              <p className="setting-value">{email}</p>
            </div>
          </div>

          <div className="settings-section">
            <h3>Appearance</h3>
            <div className="setting-item theme-toggle-container">
              <label>Dark Mode</label>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={isDarkMode}
                  onChange={handleThemeToggle}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="setting-item theme-toggle-container" style={{ marginTop: "1rem" }}>
              <label>Use Team Colors</label>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={useTeamColors}
                  onChange={handleTeamColorsToggle}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
            <p style={{ fontSize: "0.9rem", color: "var(--color-text-secondary)", marginTop: "0.5rem" }}>
              When enabled, team screens will display your team's colors. When disabled, uses standard UI colors.
            </p>
          </div>

          <div className="settings-section">
            <h3>Game Settings</h3>
            {/* Injury Report Preference */}
            <div>
              <div className="form-group">
                <label>Injury Report in Game Results</label>
                <p style={{ fontSize: "0.9rem", color: "var(--color-text-secondary)", marginBottom: "1rem" }}>
                  Choose what injury information to display after each game.
                </p>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                <label style={{ display: "flex", alignItems: "center", gap: "0.75rem", cursor: "pointer", padding: "0.5rem", borderRadius: "6px", transition: "background 0.2s", backgroundColor: injuryReportSetting === "yourTeam" ? "rgba(18, 82, 210, 0.15)" : "transparent" }}>
                  <input
                    type="radio"
                    name="injuryReport"
                    value="yourTeam"
                    checked={injuryReportSetting === "yourTeam"}
                    onChange={(e) => handleInjuryReportChange(e.target.value)}
                  />
                  <span>Just your team's injuries</span>
                </label>

                <label style={{ display: "flex", alignItems: "center", gap: "0.75rem", cursor: "pointer", padding: "0.5rem", borderRadius: "6px", transition: "background 0.2s", backgroundColor: injuryReportSetting === "bothTeams" ? "rgba(18, 82, 210, 0.15)" : "transparent" }}>
                  <input
                    type="radio"
                    name="injuryReport"
                    value="bothTeams"
                    checked={injuryReportSetting === "bothTeams"}
                    onChange={(e) => handleInjuryReportChange(e.target.value)}
                  />
                  <span>Both teams' injuries</span>
                </label>

                <label style={{ display: "flex", alignItems: "center", gap: "0.75rem", cursor: "pointer", padding: "0.5rem", borderRadius: "6px", transition: "background 0.2s", backgroundColor: injuryReportSetting === "none" ? "rgba(18, 82, 210, 0.15)" : "transparent" }}>
                  <input
                    type="radio"
                    name="injuryReport"
                    value="none"
                    checked={injuryReportSetting === "none"}
                    onChange={(e) => handleInjuryReportChange(e.target.value)}
                  />
                  <span>No injury report</span>
                </label>
              </div>
            </div>
          </div>

          <div className="settings-section">
            <h3>Change Password</h3>
            <form onSubmit={handleChangePassword}>
              <div className="form-group">
                <label htmlFor="current-password">Current Password</label>
                <input
                  type="password"
                  id="current-password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  placeholder="Enter your current password"
                />
              </div>

              <div className="form-group">
                <label htmlFor="new-password">New Password</label>
                <input
                  type="password"
                  id="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  maxLength={32}
                  required
                  placeholder="Enter your new password"
                />
                <p className="character-hint">Password must be 8-32 characters</p>
              </div>

              <div className="form-group">
                <label htmlFor="confirm-password">Confirm New Password</label>
                <input
                  type="password"
                  id="confirm-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  maxLength={32}
                  required
                  placeholder="Confirm your new password"
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? "Updating..." : "Change Password"}
              </button>
            </form>
            {message && (
              <div className={`message ${messageType}`}>
                {message}
              </div>
            )}
          </div>

          <div className="settings-section delete">
            <h3>Delete Account</h3>
            <div className="delete">
              <p>Once you delete your account, it cannot be recovered. Please be certain before deleting your account.</p>
              <button
                onClick={handleDeleteAccount}
                className="btn btn-delete"
                disabled={loading}
              >
                Delete Account
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Settings;
